import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormControlStatus,
  FormGroupDirective,
  NgControl,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import * as uuid from 'uuid';
import {
  EoFormControlConfig,
  IEoFormControlConfig,
  IFormComponent,
  IFormValidationError,
} from '../eo-form/eo-form.config';
import { defaultFormValidationError } from './eo-base-form-control.config';

export type EoFormControlType = '';

@Component({
  selector: 'eo-base-form-control',
  templateUrl: './eo-base-form-control.component.html',
  styleUrl: './eo-base-form-control.component.scss',
})
export class EoBaseFormControlComponent
  implements OnInit, OnDestroy, IFormComponent, ControlValueAccessor
{
  private cdr = inject(ChangeDetectorRef);

  public subscriptions: Subscription[] = [];
  private previousStatus?: FormControlStatus;
  // TODO: add a FormConfig Input that inits the data of the form

  id: string;
  formControl!: FormControl<any>;
  locale: any;

  @Input() hidden = false;
  @Input() labelVisible = true;
  @Input() removable?: boolean;
  @Input() tabIndex = 1;
  @Input() styleClass?: string;
  @Input() label?: string;
  @Input() help?: string;
  @Input() language?: string;
  @Output() statusChange = new EventEmitter<FormControlStatus>();
  public loading = false;

  _errors?: IFormValidationError;
  @Input() set errors(val: IFormValidationError | undefined) {
    if (!val) {
      this._errors = defaultFormValidationError;
      return;
    }
    this._errors = { ...this._errors, ...val };
  }
  get errors() {
    return this._errors;
  }

  protected _config?: EoFormControlConfig;
  @Input() set config(val: IEoFormControlConfig | undefined | any) {
    this._config = undefined;
    if (!val) return;
    this.config = val;
    this.injectFormControl();
  }
  get config() {
    return this._config;
  }

  _notApplicable: boolean = false;
  @Input() set notApplicable(val: boolean) {
    this._notApplicable = val;
    if (val) {
      if (this.formControl?.value != '###NOT_APPLICABLE###') {
        this.formControl?.patchValue('###NOT_APPLICABLE###');
        // this.formControl?.disable();
      }
    } else {
      this.formControl?.patchValue(null);
      // this.formControl?.enable();
    }
    this.formControl?.updateValueAndValidity();
  }
  get notApplicable(): boolean {
    return this._notApplicable;
  }

  _canBeNotApplicable?: boolean;
  @Input() set canBeNotApplicable(val: boolean | undefined) {
    this._canBeNotApplicable = val;
  }
  get canBeNotApplicable(): boolean {
    if (this._canBeNotApplicable != undefined) return this._canBeNotApplicable;
    if (this.config?.canBeNotApplicable != undefined)
      return this.config.canBeNotApplicable;
    return false;
  }

  _notApplicableLabel?: string;
  @Input() set notApplicableLabel(val: string | undefined) {
    this._notApplicableLabel = val;
  }
  get notApplicableLabel(): string | undefined {
    if (this._notApplicableLabel != undefined) return this._notApplicableLabel;
    if (this.config?.notApplicableLabel != undefined)
      return this.config.notApplicableLabel;
    return 'Μη εφαρμόσιμο';
  }

  /**
   * @deprecated Buggy event emitter, use onChange instead
   */
  @Output() change: EventEmitter<any> = new EventEmitter();

  /**
   * @deprecated Buggy event emitter, use onBlur instead
   */
  @Output() blur: EventEmitter<any> = new EventEmitter();

  /**
   * @deprecated Buggy event emitter, use onFocus instead
   */
  @Output() focus: EventEmitter<any> = new EventEmitter();
  @Output() onRemove: EventEmitter<any> = new EventEmitter();
  @Output() onChange: EventEmitter<any> = new EventEmitter();
  @Output() onBlur: EventEmitter<any> = new EventEmitter();
  @Output() onFocus: EventEmitter<any> = new EventEmitter();

  constructor(private injector?: Injector) {
    this.id = uuid.v4();
  }

  ngOnInit() {
    this.injectFormControl();
    this.subscriptions = [this.getStatusChangesSub$()];
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s?.unsubscribe());
  }

  protected onRemoveHandler(): void {
    this.onRemove.emit();
    if (this.config?.onRemove) this.config.onRemove();
  }

  protected onChangeHandler(event: any) {
    this.onChange.emit(event);
    this.config?.onChange?.(event);
  }

  protected onBlurHandler(event: any) {
    this.onBlur.emit(event);
    this.config?.onBlur?.(event);
  }

  protected onFocusHandler(event: any) {
    this.onFocus.emit(event);
    this.config?.onFocus?.(event);
  }

  private injectFormControl() {
    if (!this.injector) return;

    const ngControl = this.injector.get(NgControl);

    if (ngControl instanceof FormControlName) {
      this.formControl = this.injector
        .get(FormGroupDirective)
        .getControl(ngControl);
    } else {
      this.formControl = (ngControl as FormControlDirective)
        .form as FormControl;
    }
  }

  public validate(): boolean {
    return this.formControl?.valid ?? false;
  }

  isRequired(): boolean {
    return this.formControl?.hasValidator(Validators.required) ?? false;
  }

  // implement ControlValueAccessor start ------------------
  writeValue(obj: any): void {
    if (obj === '###NOT_APPLICABLE###') this.notApplicable = true;
  }
  registerOnChange(fn: any): void {}
  registerOnTouched(fn: any): void {}
  // implement ControlValueAccessor end ------------------

  upsert(arr: any[], newArr: any[], prop: string) {
    newArr.forEach((e) => {
      arr = [...arr.filter((obj) => obj[prop] !== e[prop]), { ...e }];
    });
    return arr;
  }

  private getStatusChangesSub$(): Subscription {
    return this.formControl?.statusChanges?.subscribe((status) => {
      if (this.previousStatus != status) {
        this.previousStatus = status;
        this.statusChange.emit(status);
      }
    });
  }
  protected getStyleClass(): string | undefined {
    return this.styleClass ?? this.config?.styleClass;
  }
  protected getTabIndex(): number {
    return this.tabIndex ?? this.config?.tabIndex;
  }
  protected getLabel(): string {
    return this.label ?? this.config?.label ?? '';
  }
  protected getHelp(): string {
    return this.help ?? this.config?.help ?? '';
  }
  protected getFormControl(): FormControl<any> {
    return this.formControl ?? this.config.control;
  }
  protected getErrors(): IFormValidationError | undefined {
    return {
      ...defaultFormValidationError,
      ...this.config?.errors,
      ...this.errors,
    };
  }
  protected isLoading(): boolean {
    return this.loading;
  }
  protected getConfig(): IEoFormControlConfig | undefined {
    return this.config;
  }

  protected isVisible(): boolean {
    return !this.hidden && this.labelVisible;
  }

  protected isRemovable(): boolean {
    if (this.removable != undefined) return this.removable;
    if (this.config?.removable != undefined) return this.config.removable;
    return false;
  }

  protected getRemovableClass(): string {
    return this.isRemovable() ? 'flex gap-2' : '';
  }
}
